import { makeStyles } from '@material-ui/styles'
import { RootState } from 'app/store'
import ModalForm from 'core/elements/modal/ModalForm'
import useListAction from 'core/hooks/useListAction'
import useParams from 'core/hooks/useParams'
import useUpdateAction from 'core/hooks/useUpdateAction'
import { Route } from 'core/plugins/route'
import { SessionState, sessionStoreKey } from 'core/session/sessionReducers'
import Theme from 'core/themes/model'
import { routeManager } from 'core/utils/routes'
import ServiceUnhealthyInfo from 'openstack/components/common/ServiceUnhealthyInfo'
import { prop } from 'ramda'
import { useCallback } from 'react'
import { useSelector } from 'react-redux'
import useReactRouter from 'use-react-router'
import { createSubnet, listNetworks } from '../actions'
import { getIPv6AdditionalFields } from '../IPv6AddressConfigurationModePicklist'
import { AdvancedSubnetFields, BasicSubnetFields } from '../SubnetFormFields'
import { INetworkDetailsPageTabs } from './model'

interface Props {
  addRoute: Route
  dnsDomain?: string
}

export default function CreateSubnetModal({ addRoute, dnsDomain }: Props) {
  const { history, match } = useReactRouter()
  const { id: networkId } = match.params
  const session = useSelector<RootState, SessionState>(prop(sessionStoreKey))
  const { features } = session
  const isVmware = features?.experimental?.pmov2_du_type === 'vmware'

  const defaultParams = {
    name: '',
    ipVersion: 4,
    cidr: '',
    gatewayIp: '',
    disableGateway: false,
    allocationPools: [],
    dnsNameServers: [],
    hostRoutes: [],
    enableDhcp: true,
    dns_publish_fixed_ip: false,
    ipv6AddressConfigurationMode: '',
    dns_domain: dnsDomain,
  }
  const { params, getParamsUpdater, setParams, updateParams } = useParams(defaultParams)

  const { reload } = useListAction(listNetworks, {})
  const { update, updating, error, reset } = useUpdateAction(createSubnet)
  const parentRoute = isVmware
    ? routeManager.routes.openstack.vmwareNetworkDetails
    : routeManager.routes.openstack.networkDetails

  const createSubnetFn = async (params, networkId) => {
    const allocationPools = params.allocationPools.map((entry) => {
      const [start, end] = entry.replace(/\s/g, '').split('-')
      return {
        start,
        end,
      }
    })
    const hostRoutes = params.hostRoutes.map((entry) => {
      const [cidr, nexthop] = entry.replace(/\s/g, '').split(',')
      return {
        destination: cidr,
        nexthop,
      }
    })
    const body = {
      subnet: {
        name: params.name,
        network_id: networkId,
        ip_version: params.ipVersion,
        cidr: params.cidr,
        gateway_ip: params.disableGateway ? null : params.gatewayIp ? params.gatewayIp : undefined,
        allocation_pools: allocationPools.length ? allocationPools : undefined,
        dns_nameservers: params.dnsNameServers.length ? params.dnsNameServers : undefined,
        host_routes: hostRoutes.length ? hostRoutes : undefined,
        enable_dhcp: params.enableDhcp,
        dns_publish_fixed_ip: params.dns_publish_fixed_ip,
        ...getIPv6AdditionalFields(params),
      },
    }
    const { success } = await update({ body })
    return { success }
  }

  const submitForm = useCallback(async () => {
    const { success } = await createSubnetFn(params, networkId)
    if (success) {
      reload(true, true)
      handleClose()
    }
  }, [params, networkId])

  const handleClose = () => {
    setParams(defaultParams)
    reset()
    history.push(
      parentRoute.path({
        id: networkId,
        tab: INetworkDetailsPageTabs.Subnets,
      }),
    )
  }

  return (
    <ModalForm
      route={addRoute}
      title={`Create Subnet`}
      onSubmit={submitForm}
      onClose={handleClose}
      submitting={updating}
      error={error}
      submitTitle={`Create Subnet`}
    >
      <>
        <ServiceUnhealthyInfo action="Subnet Creation" network />
        <BasicSubnetFields params={params} getParamsUpdater={getParamsUpdater} />
        <AdvancedSubnetFields params={params} updateParams={updateParams} />
      </>
    </ModalForm>
  )
}
